import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import {
  createReglementArbitrage,
  updateReglementArbitrage,
} from "../../../services/reglementarbitrageServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  FaSave,
  FaTimes,
  FaBookOpen,
  FaLink,
  FaImage,
  FaSortNumericDown,
  FaExternalLinkAlt,
} from "react-icons/fa";

function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function normalizeImageUrl(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
}

function FieldLabel({ icon, children, required = false }) {
  return (
    <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
      <span className="text-slate-500 dark:text-slate-400">{icon}</span>
      <span>{children}</span>
      {required ? <span className="text-rose-500">*</span> : null}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  min,
  step,
  autoFocus = false,
}) {
  return (
    <input
      data-autofocus={autoFocus ? true : undefined}
      type={type}
      value={value}
      onChange={onChange}
      min={min}
      step={step}
      placeholder={placeholder}
      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 10 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
    />
  );
}

export default function ReglementArbitrageModal({
  isActive,
  onClose,
  refreshReglements,
  mode = "create",
  reglementData = null,
}) {
  const { addNotification } = useNotification();

  const titleId = useId();
  const descriptionId = useId();

  const isEdit = mode === "edit";
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    lien_url: "",
    lien_url_image: "",
    display_order: "",
  });

  useEffect(() => {
    if (isEdit && reglementData) {
      setFormData({
        title: reglementData.title || "",
        content: reglementData.content || "",
        lien_url: reglementData.lien_url || "",
        lien_url_image: reglementData.lien_url_image || "",
        display_order:
          reglementData.display_order !== undefined &&
          reglementData.display_order !== null
            ? String(reglementData.display_order)
            : "",
      });
    } else {
      setFormData({
        title: "",
        content: "",
        lien_url: "",
        lien_url_image: "",
        display_order: "",
      });
    }
  }, [isEdit, reglementData, isActive]);

  const previewLink = useMemo(
    () => normalizeUrl(formData.lien_url),
    [formData.lien_url]
  );

  const previewImage = useMemo(
    () => normalizeImageUrl(formData.lien_url_image),
    [formData.lien_url_image]
  );

  const contentLength = formData.content.trim().length;

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const title = formData.title.trim();
    const content = formData.content.trim();
    const lien_url = formData.lien_url.trim();
    const lien_url_image = formData.lien_url_image.trim();
    const display_order = formData.display_order.trim();

    if (!title) {
      addNotification("Le titre est obligatoire.", "error");
      return;
    }

    if (!content) {
      addNotification("Le contenu est obligatoire.", "error");
      return;
    }

    const payload = {
      title,
      content,
      lien_url: lien_url || null,
      lien_url_image: lien_url_image || null,
      display_order: display_order !== "" ? Number(display_order) : undefined,
    };

    if (
      payload.display_order !== undefined &&
      (!Number.isFinite(payload.display_order) || payload.display_order < 0)
    ) {
      addNotification("L'ordre doit être un nombre positif.", "error");
      return;
    }

    try {
      setIsSubmitting(true);

      let result = null;

      if (isEdit && reglementData?.id) {
        result = await updateReglementArbitrage({
          id: reglementData.id,
          ...payload,
        });
      } else {
        result = await createReglementArbitrage(payload);
      }

      if (!result) {
        throw new Error("Réponse invalide du serveur");
      }

      addNotification(
        isEdit
          ? "Règlement d'arbitrage modifié avec succès."
          : "Règlement d'arbitrage créé avec succès.",
        "success"
      );

      await refreshReglements?.();
      onClose?.();
    } catch (error) {
      console.error(
        "Erreur lors de l'enregistrement du règlement d'arbitrage :",
        error
      );
      addNotification(
        "Erreur lors de l'enregistrement du règlement d'arbitrage.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isActive}
      onClose={handleClose}
      size="3xl"
      labelledById={titleId}
      describedById={descriptionId}
    >
      <ModalHeader
        id={titleId}
        title={isEdit ? "Modifier le règlement" : "Créer un règlement"}
        subtitle={
          isEdit
            ? "Mettez à jour le contenu, les liens et l’ordre d’affichage."
            : "Ajoutez un nouveau règlement d’arbitrage pour les arbitres et commissaires sportifs."
        }
        onClose={handleClose}
      />

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody id={descriptionId}>
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr),280px]">
                <div className="space-y-5">
                  <div>
                    <FieldLabel icon={<FaBookOpen />} required>
                      Titre du règlement
                    </FieldLabel>
                    <Input
                      autoFocus
                      value={formData.title}
                      onChange={handleChange("title")}
                      placeholder="Ex. Arbitrage benjamins, règlement commissaires sportifs..."
                    />
                  </div>

                  <div>
                    <FieldLabel icon={<FaSortNumericDown />}>
                      Ordre d’affichage
                    </FieldLabel>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.display_order}
                      onChange={handleChange("display_order")}
                      placeholder="Ex. 1"
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Laisse vide pour placer automatiquement le règlement à la
                      fin de la liste.
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Aperçu rapide
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
                      <FaBookOpen />
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-slate-900 dark:text-white">
                        {formData.title.trim() || "Sans titre"}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Ordre : {formData.display_order || "auto"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-xs text-slate-600 dark:text-slate-300">
                    <div>
                      Contenu :{" "}
                      <span className="font-semibold">{contentLength}</span>{" "}
                      caractère{contentLength > 1 ? "s" : ""}
                    </div>
                    <div>
                      Lien :{" "}
                      <span className="font-semibold">
                        {formData.lien_url.trim() ? "oui" : "non"}
                      </span>
                    </div>
                    <div>
                      Image :{" "}
                      <span className="font-semibold">
                        {formData.lien_url_image.trim() ? "oui" : "non"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <FieldLabel icon={<FaBookOpen />} required>
                Contenu
              </FieldLabel>
              <Textarea
                value={formData.content}
                onChange={handleChange("content")}
                rows={12}
                placeholder="Saisissez ici le contenu du règlement d’arbitrage..."
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Tu peux coller ici un texte brut ou du contenu enrichi simple
                  si ton affichage front l’accepte.
                </p>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {contentLength} caractère{contentLength > 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),340px]">
              <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <FieldLabel icon={<FaLink />}>Lien externe</FieldLabel>
                  <Input
                    value={formData.lien_url}
                    onChange={handleChange("lien_url")}
                    placeholder="https://..."
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Lien optionnel vers un document PDF, une page officielle ou
                    une ressource complémentaire.
                  </p>

                  {previewLink ? (
                    <div className="mt-3">
                      <a
                        href={previewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                      >
                        <FaExternalLinkAlt className="text-xs" />
                        Ouvrir le lien
                      </a>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <FieldLabel icon={<FaImage />}>Lien image / visuel</FieldLabel>
                  <Input
                    value={formData.lien_url_image}
                    onChange={handleChange("lien_url_image")}
                    placeholder="https://... ou /uploads/..."
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    URL optionnelle utilisée pour illustrer le règlement côté
                    front.
                  </p>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  <FaImage className="text-slate-500 dark:text-slate-400" />
                  Aperçu visuel
                </div>

                {previewImage ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                    <img
                      src={previewImage}
                      alt={formData.title || "Aperçu du règlement"}
                      className="h-56 w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
                    Aucun visuel renseigné
                  </div>
                )}

                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  L’aperçu s’affiche automatiquement si l’URL image est valide.
                </p>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {isSubmitting
              ? "Enregistrement..."
              : isEdit
              ? "Enregistrer les modifications"
              : "Créer le règlement"}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}